// Text.tsx
import clsx from 'clsx';
import React from 'react';
import styles from './Text.module.scss';

type Props<T extends React.ElementType = 'span'> = {
  tag?: T;
  className?: string;
  children: string;
  isBold?: boolean;
  isSemiBold?: boolean;
  isFormattedText?: boolean;
} & Omit<React.ComponentPropsWithoutRef<T>, 'children' | 'className'>;

const tokenize = (str: string, isBold?: boolean, isSemiBold?: boolean): React.ReactNode[] => str
  .split(/(\*\*.+?\*\*|\*\.\..+?\.\.\*|\[[^\]]+\]\([^)]+\))/g)
  .filter(Boolean)
  .map((chunk) => {
    // use kalnia font for isBold text unless isBold prop is true
    if (/^\*\*.+\*\*$/.test(chunk)) {
      return (
        <span
          key={chunk.slice(2, -2)}
          className={clsx(styles.tokenizeText, {
            isBold,
            isSemiBold,
            'font-kalnia': !isBold && !isSemiBold,
          })}
        >
          {chunk.slice(2, -2)}
        </span>
      );
    }

    if (/^\*\.\..+\.\.\*$/.test(chunk)) {
      return (
        <span
          key={chunk.slice(3, -3)}
          className={styles.dottedUnderline}
        >
          {chunk.slice(3, -3)}
        </span>
      );
    }

    const linkMatch = chunk.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={linkMatch[2]}
          href={linkMatch[2]}
          target='_blank'
          rel='noopener noreferrer'
          className='underline'
        >
          {linkMatch[1]}
        </a>
      );
    }
    return chunk;
  });

function Text<T extends React.ElementType = 'span'>({
  tag,
  className,
  children,
  isBold,
  isSemiBold,
  isFormattedText,
  ...rest
}: Props<T>) {
  if (!children) return null;
  const text = children
    .replace(/\\r\\n|\\n/g, '\n')
    .replace(/&nbsp;/g, '\u00A0')
    .replace(/&#8209;/g, '\u2011');
  const Component = tag || 'span';
  if (isFormattedText) {
    const formattedText = children
      .replace(/\/n/g, '<br/>')
      .replace(/&nbsp;/g, '\u00A0')
      .replace(/&#8209;/g, '\u2011');
    return (
      <Component
        {...rest}
        className={clsx(className)}
        dangerouslySetInnerHTML={{
          __html: formattedText,
        }}
      />
    );
  }
  return (
    <Component
      {...rest}
      className={clsx(styles.text, className)}
      style={{ ...(rest as any).style }}
    >
      {tokenize(text, isBold, isSemiBold)}
    </Component>
  );
}

export default Text;
